import { useEffect, useRef, useState } from 'react';
import { useLazyLoopVideo } from '@/hooks/useLazyLoopVideo';
import { useNavigate } from 'react-router-dom';
import { IoChevronForward } from 'react-icons/io5';
import type { IconType } from 'react-icons';
import type { CasinoGameLite } from '@/types/casino';
import { getPreviewVideo } from './gamePreviewVideos';

interface HomeCasinoRailProps {
  title: string;
  /** Supplied banner artwork (e.g. /images/casino-banners/hot.jpg) — it
   * already carries the section's own name/"View All" art, so it replaces
   * the plain yellow ribbon entirely. Sections with no banner of their own
   * (e.g. Recent) fall back to that ribbon — pass `icon` for those. */
  bannerSrc?: string;
  /** Supplied per-card badge art (e.g. /images/casino-banners/inside-hot.webp)
   * — replaces the plain CSS pill with the section's own stamp. Falls back
   * to `icon` + `title` as a text pill when there's no badge art. */
  badgeSrc?: string;
  icon?: IconType;
  games: CasinoGameLite[];
  isLoading: boolean;
  onGameClick: (game: CasinoGameLite) => void;
  /** Where tapping the header (the banner's own "View All", or the ribbon's
   * chevron) leads — the casino lobby, already on this section's
   * category/view. */
  href: string;
  /** Plays a looping preview clip instead of the static thumbnail for any
   * game with one — this rail's own clip set (see gamePreviewVideos.ts);
   * Hot and Recommended each pass their own map. */
  previewVideos?: Record<string, string>;
  /** 2 stacks the cards in two rows (3 columns x 2 = 6 visible at once). */
  rows?: 1 | 2;
}

const CARD_WIDTH = 144; // px — keeps scrollBy's step roughly in line with a card's own w-36

function GameCard({
  game,
  title,
  badgeSrc,
  Icon,
  previewVideos,
  onClick,
}: {
  game: CasinoGameLite;
  title: string;
  badgeSrc?: string;
  Icon?: IconType;
  previewVideos?: Record<string, string>;
  onClick: () => void;
}) {
  const previewVideo = getPreviewVideo(game.name, previewVideos);
  // One of the supplied clips (Dragon Tiger) came out blank — this catches
  // that (or any other clip that fails to load/decode) and drops back to
  // the static thumbnail instead of showing an empty card.
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { loaded, onLoadedData } = useLazyLoopVideo(videoRef, !!previewVideo && !videoFailed);

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative w-36 shrink-0 overflow-hidden rounded-lg bg-[#12263A] text-left shadow-sm [contain:layout_paint]"
    >
      {/* Same small badge every card in the reference carries — this
          section's own stamp, supplied art when we have it, else a plain
          text pill. */}
      {badgeSrc ? (
        <img
          src={badgeSrc}
          alt={title}
          className="absolute left-1 top-1 z-10 h-5 w-auto drop-shadow"
        />
      ) : (
        <span
          className="absolute left-1 top-1 z-10 flex items-center gap-0.5 rounded-full bg-gradient-to-b from-[#f87171] to-[#b91c1c] px-1.5 py-0.5 text-[0.6rem] font-extrabold uppercase text-white shadow"
          style={{ textShadow: '0 1px 1px rgba(0,0,0,0.35)' }}
        >
          {Icon && <Icon className="text-[0.65rem]" />}
          {title}
        </span>
      )}

      <div className="flex aspect-[4/5] items-center justify-center">
        {previewVideo && !videoFailed ? (
          <video
            ref={videoRef}
            // src is only set once the card is near the viewport (see
            // useLazyLoopVideo); #t=0.001 paints the first frame while paused.
            src={loaded ? `${previewVideo}#t=0.001` : undefined}
            preload="metadata"
            loop
            muted
            playsInline
            className="h-full w-full object-cover"
            onLoadedData={onLoadedData}
            onError={() => loaded && setVideoFailed(true)}
          />
        ) : game.gameImage ? (
          <img
            src={game.gameImage}
            alt={game.name}
            loading="lazy"
            className="h-full w-full object-cover"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = '/images/casino-card-placeholder.webp';
            }}
          />
        ) : (
          <span className="text-3xl font-bold text-primary">
            {game.name.slice(0, 1).toUpperCase()}
          </span>
        )}
      </div>

    </button>
  );
}

/**
 * "Hot" / "Recommended" / "Fish" / "Recent" home-page carousels: a header
 * (supplied banner artwork, or a plain yellow ribbon when there's no banner)
 * that opens the full list, a single Next arrow, and a horizontally
 * scrolling row of game cards underneath.
 *
 * The row is rendered twice back to back (a real set + an identical clone)
 * so Next always has somewhere forward to go — like a slot reel, not a
 * scrollbar that snaps back to 0. Once the scroll passes the boundary into
 * the clone, a scroll listener silently rewinds it by exactly one set's
 * width the moment scrolling settles; because the clone is pixel-identical
 * to the real set, that rewind lands on the same visual frame and is
 * invisible, leaving Next with fresh room to keep going forever.
 */
export default function HomeCasinoRail({
  title,
  bannerSrc,
  badgeSrc,
  icon: Icon,
  games: allGames,
  isLoading,
  onGameClick,
  href,
  previewVideos,
  rows = 1,
}: HomeCasinoRailProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Two rows fill column by column, so an odd count would start the loop
  // clone on the bottom row and misalign the set boundary — keep it even.
  const games = rows === 2 && allGames.length % 2 ? allGames.slice(0, -1) : allGames;
  const canLoop = games.length > 3 * rows;

  const scrollNext = () => {
    scrollerRef.current?.scrollBy({ left: CARD_WIDTH * 3, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !canLoop) return;

    let settleTimer: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(settleTimer);
      // Wait for scrolling to actually stop (covers both the arrow's smooth
      // animation and a manual swipe) before rewinding, so we never fight
      // an in-flight scroll.
      settleTimer = setTimeout(() => {
        const cloneStart = el.children[games.length] as HTMLElement | undefined;
        if (!cloneStart) return;
        const setWidth =
          cloneStart.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
        if (setWidth > 0 && el.scrollLeft >= setWidth) {
          // The row has `scroll-behavior: smooth` (for the arrow/swipe scroll
          // itself), which the CSSOM spec also applies to a plain `scrollLeft`
          // assignment — without this override, the "invisible" rewind was
          // instead animating smoothly all the way back, which is exactly the
          // "scrolls right, then slides back left on its own" bug this fixes.
          el.style.scrollBehavior = 'auto';
          el.scrollLeft -= setWidth;
          el.style.scrollBehavior = '';
        }
      }, 120);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      clearTimeout(settleTimer);
    };
  }, [canLoop, games.length]);

  if (!isLoading && games.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-md">
      {bannerSrc ? (
        // Cropping this used to clip the flame/crown tips — the art fills
        // basically its whole canvas top to bottom (checked pixel-by-pixel),
        // so there's no safe band to trim. Full, uncropped image instead.
        <button type="button" onClick={() => navigate(href)} className="block w-full active:opacity-90">
          <img src={bannerSrc} alt={title} className="w-full object-contain" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => navigate(href)}
          className="flex h-10 w-full items-center justify-between gap-2 bg-primary pl-3 pr-3 text-sm font-bold text-black"
        >
          <span className="flex items-center gap-1">
            {Icon && <Icon className="text-base" />}
            {title}
          </span>
          <IoChevronForward className="text-base" />
        </button>
      )}

      <div className="relative">
        <div
          ref={scrollerRef}
          className={`scrollbar-hide overflow-x-auto scroll-smooth gap-2 p-2 pr-6 ${
            rows === 2 ? 'grid grid-flow-col grid-rows-2 auto-cols-max' : 'flex'
          }`}
        >
          {isLoading
            ? Array.from({ length: 3 * rows + 1 }).map((_, index) => (
                <div
                  key={index}
                  className="aspect-[4/5] w-36 shrink-0 animate-pulse rounded-lg bg-[#e6e8e9]"
                />
              ))
            : [...games, ...(canLoop ? games : [])].map((game, index) => (
                <GameCard
                  // Clones (index >= games.length) need their own key, not
                  // the same id as the real card earlier in the list.
                  key={index < games.length ? game.id : `loop-${game.id}`}
                  game={game}
                  title={title}
                  badgeSrc={badgeSrc}
                  Icon={Icon}
                  previewVideos={previewVideos}
                  onClick={() => onGameClick(game)}
                />
              ))}
        </div>

        {/* One Next arrow, per the reference — not a prev/next pair. Just
            the round button, no vertical rail behind it. */}
        {canLoop && (
          <button
            type="button"
            onClick={scrollNext}
            aria-label={`Next ${title} games`}
            className="absolute right-1 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-gradient-to-b from-[#fff8e1] to-[#ffd766] text-black shadow-md ring-1 ring-[#e8b923]"
          >
            <IoChevronForward />
          </button>
        )}
      </div>
    </section>
  );
}
